import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { StyleBreadthContent } from './landingProofContent';

interface LandingStyleBreadthProofProps {
  content: StyleBreadthContent;
}

export function LandingStyleBreadthProof({
  content,
}: LandingStyleBreadthProofProps) {
  return (
    <section className="relative overflow-hidden border-t border-stone bg-alabaster px-6 py-24 md:py-32">
      <div className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-white to-transparent" />
      <div className="relative mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.84fr_1.16fr]">
        <div className="space-y-8">
          <div className="inline-flex items-center rounded-full border border-stone bg-white/85 px-4 py-2 text-xs font-mono uppercase tracking-[0.28em] text-sand shadow-soft">
            {content.eyebrow}
          </div>
          <div className="space-y-5">
            <h2 className="max-w-xl text-4xl font-serif font-light tracking-tight text-charcoal md:text-6xl">
              {content.title}
            </h2>
            <p className="max-w-xl text-lg font-light leading-relaxed text-sand md:text-xl">
              {content.description}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {content.proofTags.map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-stone bg-white/80 px-4 py-2 text-sm font-light text-charcoal/80 shadow-soft"
              >
                {tag}
              </span>
            ))}
          </div>
          <Link
            to="/virtual-staging/styles"
            className="inline-flex items-center gap-2 border-b border-sand/30 pb-1 text-base font-light text-sand transition-all duration-300 hover:border-terracotta/50 hover:text-terracotta"
          >
            Explore all 21 styles
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          {content.styles.map((style, index) => (
            <article
              key={style.name}
              className={`group overflow-hidden rounded-[2rem] border border-stone bg-white shadow-soft transition-transform duration-500 hover:-translate-y-1 hover:shadow-soft-xl ${
                index % 2 === 1 ? 'sm:translate-y-8' : ''
              }`}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={style.image}
                  alt={`${style.name} style preview`}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="space-y-4 p-6">
                <div className="flex items-center justify-between gap-3">
                  <span className={`rounded-full px-3 py-1 text-xs font-mono uppercase tracking-[0.2em] ${style.accentClass}`}>
                    {style.name}
                  </span>
                  <span className="text-xs font-mono uppercase tracking-[0.2em] text-sand">
                    Same room
                  </span>
                </div>
                <div>
                  <h3 className="text-2xl font-serif font-light text-charcoal">
                    {style.name}
                  </h3>
                  <p className="mt-2 text-sm font-light leading-relaxed text-sand">
                    {style.audience}
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
